import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import cn from 'classnames';
import { format } from 'date-fns';
import clone from 'lodash.clonedeep';
import isEmpty from 'lodash.isempty';
import isNumber from 'lodash.isnumber';
import pull from 'lodash.pull';
import uniq from 'lodash.uniq';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Alert from '../../components/Alert';
import { useNotificationStore } from '../../stores/notificationStore';
import { useUserStore } from '../../stores/userStore';
import QuestionInput from './components/QuestionInput';

const Assessment = () => {
	const queryClient = useQueryClient();
	let { formId } = useParams();
	const [defaultAnswers, setDefaultAnswers] = useState({});
	const [answers, setAnswers] = useState({});
	const [isSaving, setIsSaving] = useState(false);
	const [canTotal, setCanTotal] = useState(false);
	const { user } = useUserStore();
	const { showError, showSuccess } = useNotificationStore();
	const navigate = useNavigate();
	const location = useLocation();
	// Pages that link here can pass `state.from` to control where a successful
	// save returns to (e.g. "/cca"); otherwise fall back to /home.
	const returnTo = location.state?.from ?? '/home';
	const { isFetching, isFetched, data, isError } = useQuery({
		queryKey: ['care_quest_questions', formId],
		queryFn: async () => {
			const { data } = await axios.get('/care_quest_questions', {
				params: {
					filter: JSON.stringify({
						where: {
							care_quest_groupId: formId,
						},
						include: 'care_quest_answers',
						order: 'question_order',
					}),
				},
			});
			return data;
		},
		enabled: !!formId,
	});
	const {
		isFetched: isRespFetched,
		data: respData,
		isRefetching,
	} = useQuery({
		queryKey: ['care_quest_response_group', formId, user.id],
		queryFn: async () => {
			const { data } = await axios.get(
				`/clients/${user.id}/care_quest_response_groups`,
				{
					params: {
						filter: JSON.stringify({
							where: {
								care_quest_groupId: formId,
							},
							include: [
								{
									relation: 'care_quest_responses',
									scope: {
										include: ['care_quest_answer'],
									},
								},
								'care_quest_group',
							],
							order: 'response_dttm DESC',
							limit: 1,
						}),
					},
				},
			);
			return Array.isArray(data) && data.length > 0 ? data[0] : null;
		},
		enabled: !!formId && !!user.id && isFetched,
		retry: 0,
	});

	useEffect(() => {
		setAnswers({});
		setDefaultAnswers({});
		setCanTotal(false);
	}, [formId]);
	useEffect(() => {
		if (isFetched) {
			let isTotalable = false;
			const defaultAnswers = {};
			data.forEach((question, index) => {
				if (question.question_type === 'Multiple Choice Single Select') {
					defaultAnswers[`${question.id}`] = question.default_answer;
					isTotalable = false;
				} else if (
					question.question_type === 'Multiple Choice Multiple Select'
				) {
					defaultAnswers[`${question.id}`] = [];
					isTotalable = false;
				} else if (question.question_type === 'Yes/No') {
					defaultAnswers[`${question.id}`] = question.default_answer;
					isTotalable = false;
				} else if (question.question_type === 'Freetext response') {
					defaultAnswers[`${question.id}`] = '';
					isTotalable = isTotalable && index === data.length - 1 ? true : false;
				} else if (question.question_type === 'Numeric') {
					defaultAnswers[`${question.id}`] = question.default_answer;
					isTotalable = true;
				} else if (question.question_type === 'Wellness Scale') {
					defaultAnswers[`${question.id}`] = 0;
				} else if (question.question_type.indexOf('slider') === 0) {
					// set a default
					defaultAnswers[`${question.id}`] = 0;
					// convert from string or calculate default answer
					if (isNumber(question.default_answer)) {
						defaultAnswers[`${question.id}`] = parseInt(
							question.default_answer,
							10,
						);
					} else {
						try {
							const [, range] = question.question_type.split(':');
							const [min, max] = range.split('-');
							if (isNumber(min) && isNumber(max)) {
								defaultAnswers[`${question.id}`] = Math.round(
									(parseInt(max, 10) - parseInt(min, 10)) / 2,
								);
							}
						} catch (err) {
							// a default is set already
						}
					}
					isTotalable = false;
				} else {
					// TODO: other options as needed...
					// console.log('UNKNOWN', question)
					isTotalable = false;
				}
			});
			setCanTotal(isTotalable);
			setDefaultAnswers(defaultAnswers);
		}
	}, [isFetched, data]);

	useEffect(() => {
		if (!isRefetching && isRespFetched && respData) {
			const existingAnswers = clone(defaultAnswers);
			respData.care_quest_responses.forEach((resp) => {
				if (Array.isArray(existingAnswers[`${resp.care_quest_questionId}`])) {
					existingAnswers[`${resp.care_quest_questionId}`].push(
						resp.care_quest_answerId,
					);
				} else if (
					resp.care_quest_answerId &&
					isEmpty(resp.care_quest_answer_text)
				) {
					existingAnswers[`${resp.care_quest_questionId}`] =
						resp.care_quest_answerId;
				} else {
					existingAnswers[`${resp.care_quest_questionId}`] =
						resp.care_quest_answer_text;
				}
			});
			setAnswers(existingAnswers);
		} else if (isRespFetched && isEmpty(respData)) {
			setAnswers(defaultAnswers);
		}
	}, [isRespFetched, respData, defaultAnswers, isRefetching]);

	useEffect(() => {
		// total values
		if (isFetched && canTotal) {
			let total = 0;
			data.forEach((question, index) => {
				if (index < data.length - 1) {
					total += Number(answers[question.id]);
				} else if (
					index === data.length - 1 &&
					total != answers[`${question.id}`]
				) {
					setAnswers({
						...answers,
						[`${question.id}`]: total,
					});
				}
			});
		}
	}, [data, answers, canTotal]);

	const handleAnswerValueChange = (questionId, answer) => {
		if (answer.type === 'checkbox') {
			const chosenAnswer = [...answers[`${questionId}`]];
			const newAnswer = parseInt(answer.value);
			if (answer.checked) {
				chosenAnswer.push(newAnswer);
			} else {
				pull(chosenAnswer, newAnswer);
			}
			setAnswers({
				...answers,
				[`${questionId}`]: uniq(chosenAnswer),
			});
		} else if (answer.type === 'file') {
			setAnswers({
				...answers,
				[`${questionId}`]: answer.files,
			});
		} else if (answer.type === 'select-one') {
			setAnswers({
				...answers,
				[`${questionId}`]: parseInt(answer.value),
			});
		} else {
			setAnswers({
				...answers,
				[`${questionId}`]: answer.value,
			});
		}
	};

	const handleSaveResponse = async () => {
		const finalAnswers = [];
		const entities = data.reduce((prev, curr) => {
			prev[curr.id] = curr;
			return prev;
		}, {});
		const response_dttm = new Date();
		Object.keys(answers).forEach((questionId) => {
			// if (hideQuestion(entities[questionId])) {
			// 	return;
			// }
			let isMultiChoice = false;
			const finalAnswer = {
				care_quest_questionId: parseInt(questionId),
				care_quest_groupId: formId, // entities[questionId].care_quest_group.id,
				response_dttm,
			};
			// extract answer id from choice questions
			if (
				['Multiple Choice Single Select', 'Yes/No'].includes(
					entities[questionId].question_type,
				)
			) {
				const chosenAnswer = entities[questionId].care_quest_answers.find(
					(answer) => answer.id == answers[questionId],
				);
				if (entities[questionId].required_ind && !chosenAnswer) {
					// TODO show validation error
				} else {
					finalAnswer.care_quest_answerId = chosenAnswer.id;
				}
			} else if (
				['Multiple Choice Multiple Select'].includes(
					entities[questionId].question_type,
				)
			) {
				isMultiChoice = true;
				answers[questionId].forEach((response) => {
					const chosenAnswer = entities[questionId].care_quest_answers.find(
						(answer) => answer.id === response,
					);
					if (entities[questionId].required_ind && !chosenAnswer) {
						// TODO show validation error
					} else {
						const answer = {
							...finalAnswer,
							care_quest_answerId: chosenAnswer.id,
						};
						finalAnswers.push(answer);
					}
				});
			} else if ('document_upload' === entities[questionId].question_type) {
				const formData = new FormData();
				formData.append('data', JSON.stringify(finalAnswer));
				for (let i = 0; i < answers[questionId].length; i++) {
					formData.append('files', answers[questionId][i]);
				}
				finalAnswer.formData = formData;
			} else {
				finalAnswer.care_quest_answer_text = answers[questionId];
			}
			if (!isMultiChoice) {
				finalAnswers.push(finalAnswer);
			}
		});

		// console.log(finalAnswers);

		if (finalAnswers.length > 0) {
			setIsSaving(true);
			try {
				const { data } = await axios.post(
					`/clients/${user.id}/care_quest_response_groups`,
					{
						care_quest_groupId: formId,
						response_dttm,
						completed_by_type: 'client',
						completed_by_id: user.id,
					},
				);
				const results = await Promise.allSettled(
					finalAnswers.map(async (finalAnswer) => {
						if (finalAnswer.formData) {
							const { formData } = finalAnswer;
							const fData = JSON.parse(formData.get('data'));
							formData.set(
								'data',
								JSON.stringify({
									...fData,
									clientId: user.id,
									care_quest_response_groupId: data.id,
								}),
							);
							const response = await axios.post(
								'/care_quest_responses/upload-response-with-file',
								formData,
								{
									headers: {
										'Content-Type': 'multipart/form-data',
									},
								},
							);
							return {
								uploads: formData.getAll('files').length,
								...response.data,
							};
						} else {
							const response = await axios.post('/care_quest_responses', {
								...finalAnswer,
								clientId: user.id,
								care_quest_response_groupId: data.id,
							});
							return response.data;
						}
					}),
				);
				queryClient.invalidateQueries({
					queryKey: ['care_quest_response_group', formId, user.id],
				});
				queryClient.invalidateQueries({
					queryKey: ['incompleteRequests', user.id],
				});
				let message = 'Application module saved';
				const totalUploaded = results
					.filter((r) => r.value?.uploads)
					.reduce((sum, r) => sum + r.value.uploads, 0);
				if (totalUploaded > 0) {
					message += `, ${totalUploaded} file(s) uploaded`;
				}
				showSuccess(message);
				navigate(returnTo);
			} catch (e) {
				showError('There was a problem saving the application module');
			} finally {
				setIsSaving(false);
			}
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
	};

	return (
		<div className="application page-wide">
			<h1>Application</h1>
			<h3>
				{respData && (
					<>
						{respData?.care_quest_group?.name}{' '}
						<em className="text-base font-normal text-gray-500">
							<small>
								Last updated on{' '}
								{format(new Date(respData.response_dttm), 'PP p')}
							</small>
						</em>
					</>
				)}
			</h3>
			{isError && <Alert message="Unable to load application!" />}
			{isFetching && 'Loading...'}
			{isFetched && (
				<form onSubmit={handleSubmit}>
					<div className="grid items-end gap-4 xl:grid-cols-2 2xl:grid-cols-3">
						{data.map((question) => (
							<div
								key={`${question.care_quest_groupId}-${question.id}-${question.question_order}`}
								className={cn('form-control', {
									'xl:col-span-2 2xl:col-span-3':
										question.question_type === 'Freetext response' ||
										question.question_type ===
											'Multiple Choice Multiple Select' ||
										question.question_type === 'document_upload',
								})}
							>
								<label
									className="text-sm label"
									htmlFor={`q-${question.care_quest_groupId}.${question.id}`}
								>
									{question.question_text}
								</label>
								<QuestionInput
									id={`q-${question.care_quest_groupId}.${question.id}`}
									question={question}
									onChange={({ target }) =>
										handleAnswerValueChange(question.id, target)
									}
									chosenAnswer={answers[`${question.id}`]}
								/>
							</div>
						))}
					</div>
					<div className="mt-4 text-right">
						<button
							className="btn btn-primary"
							onClick={handleSaveResponse}
							disabled={isSaving}
						>
							{isSaving && <span className="loading loading-spinner" />}
							Save
						</button>
					</div>
				</form>
			)}
		</div>
	);
};

export default Assessment;
